import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { IMAGES } from "../../data/content";
import { CTAButton, scrollToHash, EASE } from "./ui";

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      id="top"
      ref={ref}
      className="relative min-h-screen overflow-hidden bg-ink"
      data-testid="hero"
    >
      <motion.div className="absolute inset-0" style={{ y: imgY }}>
        <motion.div
          className="h-full w-full"
          initial={{ scale: 1.08 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.8, ease: EASE }}
        >
          <motion.img
            src={IMAGES.hero}
            alt="Propriété d'exception en pierre au crépuscule, piscine à débordement"
            className="h-full w-full object-cover object-center"
            animate={{ scale: [1, 1.05, 1] }}
            transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
          />
        </motion.div>
      </motion.div>

      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(90deg, rgba(10,10,10,0.95) 0%, rgba(10,10,10,0.72) 42%, rgba(10,10,10,0.28) 75%, rgba(10,10,10,0.45) 100%)",
        }}
        aria-hidden="true"
      />
      <div className="absolute inset-0 bg-ink/45 md:hidden" aria-hidden="true" />
      <div
        className="absolute inset-x-0 bottom-0 h-48"
        style={{ background: "linear-gradient(to top, #0a0a0a 0%, transparent 100%)" }}
        aria-hidden="true"
      />

      <motion.div
        style={{ opacity: contentOpacity }}
        className="absolute bottom-[12vh] left-[clamp(24px,7vw,110px)] max-w-[760px] pr-6"
      >
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="font-sans text-[11px] font-normal uppercase tracking-[0.22em] text-champagne"
          data-testid="hero-kicker"
        >
          Private & Corporate Concierge
        </motion.p>

        <h1 className="mt-7 font-serif font-medium leading-[0.88] text-ivory text-[clamp(58px,8vw,128px)]">
          <span className="block overflow-hidden pb-1">
            <motion.span
              className="block"
              initial={{ y: "112%" }}
              animate={{ y: "0%" }}
              transition={{ duration: 0.9, delay: 0.28, ease: EASE }}
            >
              UNE DEMANDE.
            </motion.span>
          </span>
          <span className="block overflow-hidden pb-2">
            <motion.span
              className="block"
              initial={{ y: "112%" }}
              animate={{ y: "0%" }}
              transition={{ duration: 0.9, delay: 0.4, ease: EASE }}
            >
              UNE SOLUTION.
            </motion.span>
          </span>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.62, ease: EASE }}
          className="mt-8 max-w-[600px] font-sans text-base font-light leading-[1.7] text-[#a0a0a0] md:text-[18px]"
          data-testid="hero-subheading"
        >
          Un interlocuteur unique pour trouver, sourcer et coordonner les bons
          prestataires — pour votre résidence, votre entreprise, votre vie privée et
          vos demandes exceptionnelles.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.76, ease: EASE }}
          className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center"
        >
          <CTAButton onClick={() => scrollToHash("#demande")} testid="hero-cta-primary">
            Confier une demande
          </CTAButton>
          <CTAButton
            variant="secondary"
            onClick={() => scrollToHash("#services")}
            testid="hero-cta-secondary"
          >
            Découvrir nos services
          </CTAButton>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.95 }}
          className="mt-9 font-display text-[10px] font-medium tracking-[0.18em] text-muted"
          data-testid="hero-microcopy"
        >
          CLIENTS PRIVÉS · DIRIGEANTS · ENTREPRISES
        </motion.p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        className="absolute bottom-10 right-[clamp(24px,7vw,110px)] hidden items-center gap-4 md:flex"
        aria-hidden="true"
      >
        <span className="font-display text-[10px] font-medium tracking-[0.24em] text-muted">
          DÉFILER
        </span>
        <div className="relative h-14 w-px overflow-hidden bg-white/10">
          <motion.div
            className="absolute left-0 top-0 h-1/2 w-px bg-champagne"
            animate={{ y: ["-100%", "220%"] }}
            transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
          />
        </div>
      </motion.div>
    </section>
  );
};

export default Hero;
